export class ApiError extends Error {
  constructor(message: string, public readonly status: number, public readonly code?: string, public readonly details?: unknown) {
    super(message)
    this.name = 'ApiError'
  }
}

export type PublicConfig = {
  csrfToken: string
  configured?: boolean
  checkoutEnabled?: boolean
  paymentsConfigured?: boolean
  shippingConfigured?: boolean
  supportEmail?: string
  [key: string]: unknown
}

const base = import.meta.env.VITE_API_BASE_URL || '/api/index.php'
let configPromise: Promise<PublicConfig> | undefined
let config: PublicConfig | undefined

function endpoint(path: string) {
  const url = new URL(base, window.location.origin)
  const [route, query] = path.split('?', 2)
  url.searchParams.set('route', route)
  if (query) new URLSearchParams(query).forEach((value, key) => {
    if (key !== 'route') url.searchParams.set(key, value)
  })
  return url.toString()
}

async function parseResponse<T>(response: Response): Promise<T> {
  const contentType = response.headers.get('content-type') || ''
  if (!contentType.includes('application/json')) {
    throw new ApiError('Il servizio non è ancora disponibile. Riprova più tardi.', response.status || 503)
  }
  const result = await response.json()
  if (!response.ok) {
    throw new ApiError(result.error?.message || result.message || 'Operazione non riuscita.', response.status, result.error?.code, result.error?.details)
  }
  return result as T
}

async function loadConfig(): Promise<PublicConfig> {
  if (!configPromise) {
    configPromise = fetch(endpoint('/config'), { credentials: 'same-origin', cache: 'no-store' })
      .then(parseResponse<PublicConfig>)
      .then(value => { config = value; return value })
      .catch(error => { configPromise = undefined; throw error })
  }
  return configPromise
}

export const api = {
  config: loadConfig,
  async request<T>(path: string, options: RequestInit = {}): Promise<T> {
    const method = (options.method || 'GET').toUpperCase()
    const headers = new Headers(options.headers)
    headers.set('Accept', 'application/json')
    if (options.body && !(options.body instanceof FormData) && !headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json')
    }
    if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) {
      const current = await loadConfig()
      headers.set('X-CSRF-Token', current.csrfToken)
    }
    const result = await parseResponse<T>(await fetch(endpoint(path), {
      ...options, method, headers, credentials: 'same-origin', cache: 'no-store',
    }))
    if (method === 'POST' && /^\/auth\/(login|register|signup|logout)$/.test(path)) {
      config = undefined
      configPromise = undefined
    }
    return result
  },
  invalidateConfig() { config = undefined; configPromise = undefined },
}

export function isBackendAvailable() { return Boolean(config?.configured) }
